<!-- This component is used for INTL Switch Plan & INTL Ex-sub Payment Summary -->
<template>
    <div class="wrapper">
        <div class="padded-container">
            <div
                data-ci="summary-page"
                class="aa-layout payment-layout summary-page"
                :class="{ 'summary-page--signup-flow': isExSubscriber }"
                ref="paymentForm">
                <span v-if="!isExSubscriber" class="icon-fill-rounded__check"></span>
                <div class="summary-page__header">
                    <h3 class="summary-page__title">
                        {{ getTitle }}
                    </h3>
                    <p v-if="getSubtitle" class="summary-page__subtitle">
                        {{ getSubtitle }}
                    </p>
                </div>
                <div class="payment-layout__summary summary-page__details">
                    <OrderSummary
                        @found-price="updatePrice"
                        :coupon="displayCoupon"
                        :couponCheckPending="couponCheckPending"
                        :couponDisclaimer="couponDisclaimerCopy"
                        @applyCoupon="handleApplyCoupon"
                        @updateOrder="updateOrder"
                        @trialEndDateFormatted="updateTrialEndDate"
                        ref="orderSummary"
                        :recurly="recurly"
                        :formConfig="orderSummaryConfig"
                        :isFromDirect="isFromDirect"
                        v-if="!enhancedPaymentSummaryEnabled"></OrderSummary>
                    <BillingAgreementSummary
                        @found-price="updatePrice"
                        :coupon="displayCoupon"
                        :couponCheckPending="couponCheckPending"
                        :couponDisclaimer="couponDisclaimerCopy"
                        @applyCoupon="handleApplyCoupon"
                        @updateOrder="updateOrder"
                        @trialEndDateFormatted="updateTrialEndDate"
                        ref="orderSummary"
                        :recurly="recurly"
                        :formConfig="orderSummaryConfig"
                        :promo="this.appliedCouponCode"
                        :pricing="pricing"
                        :pageAttr="pageAttr"
                        :nextNonZeroBillingPrice="nextNonZeroBillingPrice"
                        :couponDiscountBillingDate="couponDiscountBillingDate"
                        :isFromDirect="isFromDirect"
                        v-if="enhancedPaymentSummaryEnabled"></BillingAgreementSummary>
                    <div :id="errorMessageElementId" role="alert">
                        <p v-if="formErrorMessage" class="error">
                            {{ formErrorMessage }}
                        </p>
                    </div>
                    <BillingPaymentMethod
                        :pageAttr="pageAttr"
                        :twoButtonExperience="true"
                        :errorMessageElementId="errorMessageElementId"
                        @is-payment-method-form-edit="
                            setPaymentMethodFormEnabled
                        "></BillingPaymentMethod>
                    <SummaryUpgradeDowngrade :pageAttr="pageAttr" v-if="isSwitchPlan" />
                    <p v-html="getAgreementCopy()" class="payment-layout__summary__agreement"></p>
                    <SimpleWaiveCoolingRights
                        v-if="needsSimpleWaiveCoolingRights"
                        testId="waiveOfCoolingRightsCheckbox"
                        :checkboxConfig="switchPlanWaiveCoolingRightsCheckboxConfig"
                        :errorMessage="waiveOfCoolingRightsCheckboxErrorMessage"
                        @change="setWaiveOfCoolingRights($event)" />
                    <input type="hidden" name="recurly-token" data-recurly="token" />

                    <FormButton
                        :button-config="submitConfig"
                        :button-state="submitButtonState"
                        ref="ctaSwitchPlan"
                        @click="subscribeOrUpdate($event)"></FormButton>
                </div>
            </div>
        </div>
        <transition name="fade">
            <WaitingOverlay
                :line1="$getLocale('we_are_processing_your_subscription')"
                :line2="$getLocale('this_will_not_take_long')"
                v-if="formIsProcessing || (intlSingleColumnPayment && getTransactionProcessing)" />
        </transition>
    </div>
</template>

<script>
    import { mapState, mapGetters } from 'vuex';

    import couponsMixin from 'aa/vue/plugins/couponsMixin';
    import checkoutMixin from 'aa/vue/plugins/checkoutMixin';
    import { default as multiSubSwitchPlan } from 'aa/vue/components/multisubplanpicker/switchPlanMixin';
    import commonMixins from 'aa/vue/plugins/account/commonMixins';
    import pricePrettyPrint from 'aa/vue/plugins/priceMixin';
    import currentSubscriptionMixin from 'aa/vue/components/account/currentSubscriptionMixin';

    import { OrderSummaryConfig } from 'aa/vue/FormConfig';
    import BillingAgreementSummary from 'aa/vue/components/BillingAgreementSummary';
    import SummaryUpgradeDowngrade from 'aa/vue/components/SummaryUpgradeDowngrade';
    import OrderSummary from 'aa/vue/components/OrderSummary';
    import BillingPaymentMethod from 'aa/vue/components/BillingPaymentMethod';
    import SimpleWaiveCoolingRights from './payment/SimpleWaiveCoolingRights';
    import FormButton from 'atoms/FormButton';
    import { ButtonState, default as ButtonConfig } from 'models/ButtonConfig';
    import NotificationBarModel from 'aa/vue/models/NotificationBarModel';

    import { PAGE_TYPES } from 'services/Tracking';
    import { PAGE_TYPES as AA_PAGE_TYPES } from 'aa/vue/constants/aaTrackingConstants';
    import WaitingOverlay from 'aa/vue/atoms/WaitingOverlay';
    import { PAYMENT_EXSUB_BILLING_SUMMARY } from 'helpers/featureConstants';
    import {
        REMOVE_COUPON_ON_SWITCH_PLAN,
        SINGLE_COLUMN_PAYMENT_PAGE,
        SWITCH_PLAN_WAIVE_COOLING_RIGHTS_ENABLED,
        WAIVE_COOLING_RIGHTS_ENABLED,
    } from 'aa/helpers/featureConstants';
    import { DURATION } from 'aa/vue/constants/couponConstants';

    import { RECAPTCHA_FORMS } from 'recaptcha/Recaptcha';
    import {
        setUpThreeDSecure,
        fetchStoredPaymentIfEmpty,
        isKnownLocaleMessage,
    } from './payment/signupUtil';
    import { PAYMENT_CC, PAYMENT_PAYPAL, SWITCH_DIRECTION_UPGRADE } from '../constants/shared';

    // Reuse the existing (already-translated) coupon-stacking message for the lose-promo ribbon.
    const SWITCH_PLAN_LOSE_PROMO_KEY = 'coupon_stacking_error_message';

    export default {
        name: 'Summary',
        mixins: [
            couponsMixin,
            pricePrettyPrint,
            checkoutMixin,
            commonMixins,
            multiSubSwitchPlan,
            currentSubscriptionMixin,
        ],
        components: {
            BillingAgreementSummary,
            BillingPaymentMethod,
            SimpleWaiveCoolingRights,
            FormButton,
            SummaryUpgradeDowngrade,
            OrderSummary,
            WaitingOverlay,
        },
        props: {
            setUpThreeDSecure: {
                type: Function,
                default: setUpThreeDSecure,
            },
            isFromDirect: {
                type: Boolean,
                default: false,
            },
            handleProcessedPayment: {
                type: Function,
                default: () => {},
            },
            handleProcessedPaymentError: {
                type: Function,
                default: () => {},
            },
        },
        data: function () {
            return {
                payPalToken: null,
                paymentRequestData: {},
                bodyScrollPos: 0,
                orderSummaryConfig: OrderSummaryConfig,
                couponDiscountBillingDate: null,
                losePromoRibbonShown: false,
                recaptchaAction: this.isExSubscriber
                    ? RECAPTCHA_FORMS.FORM_PAYMENT
                    : RECAPTCHA_FORMS.FORM_SWITCH_PLAN,
                paymentMethodFormEnabled: false,
                formErrorMessage: null,
                errorMessageElementId: 'summary-page-exsub-error-message',
                formIsProcessing: false,
                userAppliedCouponDuringSwitchPlan: false,
            };
        },
        computed: {
            ...mapState(['autoPromoChecked']),
            ...mapGetters('ftc', ['getTransactionProcessing']),
            ...mapGetters('plan', ['getSelectedPlan', 'isSwitchPlan']),
            ...mapGetters('storedPaymentMethod', ['hasValidStoredPaymentMethod', 'getBillingInfo']),
            ...mapGetters('user', ['isExSubscriber']),
            intlSingleColumnPayment() {
                return this.$store.getters.featureIsActive(SINGLE_COLUMN_PAYMENT_PAGE);
            },
            submitConfig() {
                const displayText =
                    this.pageAttr?.billing_summary_cta_form_submit ?? this.$getLocale('buy_now');
                return {
                    styleClass: [
                        'button',
                        this.plan && !this.paymentMethodFormEnabled ? 'primary' : 'disabled',
                    ],
                    displayText,
                    isDisabled: (!this.plan && this.paymentMethodFormEnabled) || this.isProcessing,
                };
            },
            submitButtonState() {
                if (this.isProcessing || this.paymentMethodFormEnabled) {
                    return ButtonState.Disabled;
                }

                if (this.needsSimpleWaiveCoolingRights && !this.waiveOfCoolingChecked) {
                    return ButtonState.Disabled;
                }

                return ButtonState.Enabled;
            },
            getTitle() {
                return this.isExSubscriber
                    ? (this.pageAttr.paysumhdr_exsub ?? this.pageAttr.explainer_description ?? '')
                    : (this.pageAttr.paysumhdr_switchplan ??
                          this.pageAttr.switch_plan_summary_header ??
                          '');
            },
            getSubtitle() {
                const legacy = this.pageAttr.explainer_description_subtitle;
                return this.isExSubscriber
                    ? (this.pageAttr.paysumsubhdr_exsub ?? legacy ?? '')
                    : (this.pageAttr.paysumsubhdr_switchplan ?? legacy ?? '');
            },
            currentPlan() {
                return this.$store.getters['plan/getCurrentPlan'];
            },
            shouldRemoveSwitchPlanCoupon() {
                if (!this.isSwitchPlan || !this.featureIsActive(REMOVE_COUPON_ON_SWITCH_PLAN)) {
                    return false;
                }
                // Forever coupons are preserved across switch plans. Detect via the synchronous
                // currentSubscription signal because this.coupon is null at the moment this runs
                // during pricing setup; it settles asynchronously via the set.coupon event.
                if (
                    this.currentSubscriptionHasForeverCoupon ||
                    this.coupon?.recurlyInfo?.duration === DURATION.FOREVER
                ) {
                    return false;
                }
                // A coupon explicitly applied during this switch-plan flow is preserved
                if (this.userAppliedCouponDuringSwitchPlan) {
                    return false;
                }
                // A pushed promo is explicit user intent for this switch-plan flow, so the
                // coupon must remain visible/applied.
                if (this.hasPushedPromo) {
                    return false;
                }
                return true;
            },
            displayCoupon() {
                // Empty object (not null) so downstream summary components that read `coupon.*`
                // without optional chaining do not throw; `promoString` still hides the discount
                // row when `recurlyInfo` is missing.
                return this.shouldRemoveSwitchPlanCoupon ? {} : this.coupon;
            },
            // The promo resolved for this request: pushed via ?promo= (persisted into the
            // sv_campaign cookie and resolved server-side) or carried from earlier in the flow.
            hasPushedPromo() {
                return !!this.$store.state.promo;
            },
            isUpgrade() {
                return this.plan?.switchDirection?.toLowerCase() === SWITCH_DIRECTION_UPGRADE;
            },
            switchPlanCheckboxCopy() {
                return this.pageAttr?.[
                    this.isUpgrade
                        ? 'switchplan_checkbox_copy_upgrade'
                        : 'switchplan_checkbox_copy_downgrade'
                ];
            },
            needsSwitchPlanWaiveCoolingRights() {
                return (
                    this.isSwitchPlan &&
                    this.featureIsActive(SWITCH_PLAN_WAIVE_COOLING_RIGHTS_ENABLED) &&
                    Boolean(this.switchPlanCheckboxCopy)
                );
            },
            needsSimpleWaiveCoolingRights() {
                return (
                    this.needsSwitchPlanWaiveCoolingRights ||
                    (this.featureIsActive(WAIVE_COOLING_RIGHTS_ENABLED) &&
                        this.hasValidStoredPaymentMethod)
                );
            },
            switchPlanWaiveCoolingRightsCheckboxConfig() {
                return {
                    type: 'checkbox',
                    elementId: 'switchPlanWaiveCoolingRights',
                    inputLabel: this.switchPlanCheckboxCopy,
                    wrapperClass:
                        'payment-layout__summary__waive-cooling-rights cbs-checkbox cbs-checkbox--waive-cooling-rights',
                    styleClass: 'qt-waiveCoolingRights',
                    classList:
                        'payment-layout__summary__waive-cooling-rights__checkbox cbs-checkbox-input',
                    value: false,
                };
            },
            shouldWarnLosingPromoOnSwitch() {
                if (!this.isSwitchPlan || !this.featureIsActive(REMOVE_COUPON_ON_SWITCH_PLAN)) {
                    return false;
                }
                // Don't stack on top of an explicit query-driven ribbon (e.g. coupon_stacking_error_message)
                if (this.$route.query?.notification) {
                    return false;
                }
                // Switching during a free trial forfeits the trial promotion.
                if (this.currentPlan?.on_trial) {
                    return true;
                }
                // Warn whenever the account already carries a promotion — it is lost (or
                // replaced) on switch plan, including when a ?promo= is applied to the new
                // plan — EXCEPT a forever coupon, which 2885 preserves across switch plan.
                if (!this.currentSubscriptionPromo) {
                    return false;
                }
                // A forever coupon is preserved across switch plan, so no promo is lost.
                // Detect it via the synchronous currentSubscription signal: this.coupon has
                // not settled yet when the ribbon decision runs (it loads async via set.coupon),
                // so the recurlyInfo check below cannot be relied on for the forever case.
                if (this.currentSubscriptionHasForeverCoupon) {
                    return false;
                }
                return this.coupon?.recurlyInfo?.duration !== DURATION.FOREVER;
            },
        },
        created() {
            if (!this.plan) {
                return;
            }

            const trackingParams = {
                productPricingPlan: this.plan.planType,
                pickPlanType: this.plan.planTier,
                pickPlanSku: this.plan.recurlyCode,
                productCurrentSku: this?.userProduct?.product_code ?? this.currentPlan?.recurlyCode,
                productNewSku: this.plan.recurlyCode,
                ctaText: this.$getLocale('buy_now'),
                pageType: PAGE_TYPES.PLAN_CHANGE_CONFIRMATION,
                pageUrl: window.location.href,
            };

            if (this.isFromDirect) {
                trackingParams.purchaseProduct = this.plan.recurlyCode;
                trackingParams.purchaseProductName = this.plan.titleForTracking;
                trackingParams.purchasePrice = this.plan.rawPrice;
                trackingParams.productOfferPeriod = this.plan?.trialString ?? '';
                trackingParams.purchaseQuantity = '1';
                trackingParams.purchasePromoCode = this.appliedCouponCode;
                if (this.$route.params?.directLinkFlow) {
                    trackingParams.pageType = `winback_${this.$route.params.directLinkFlow}_directLink_payment`;
                } else {
                    trackingParams.pageType = 'switchplan_directLink_payment';
                }
            }

            if (this.isExSubscriber) {
                trackingParams.pageType = AA_PAGE_TYPES.EXSUB_PAYMENT;
            }

            const trackingServiceInstance = this.$trackingService ?? this.trackingService;

            trackingServiceInstance.trackState(trackingParams);
        },
        methods: {
            handleApplyCoupon(event) {
                this.userAppliedCouponDuringSwitchPlan = true;
                this.applyCoupon(event);
            },
            // Fires the lose-promo blue ribbon at most once, when the user is confirmed to be
            // losing a promotion on switch plan. Safe to call repeatedly (e.g. from mounted for
            // the synchronous free-trial case, and from the coupon watch once the async coupon
            // settles for the coupon-strip case).
            showLosePromoRibbonIfNeeded() {
                if (this.losePromoRibbonShown || !this.shouldWarnLosingPromoOnSwitch) {
                    return;
                }
                this.losePromoRibbonShown = true;
                this.openNotification(
                    new NotificationBarModel({
                        success: true,
                        autoClose: false,
                        message: this.$getLocale(SWITCH_PLAN_LOSE_PROMO_KEY),
                    }),
                );
            },
            setPaymentMethodFormEnabled(event) {
                this.paymentMethodFormEnabled = event;
            },
            subscribeOrUpdate(e) {
                if (this.needsSimpleWaiveCoolingRights && !this.waiveOfCoolingChecked) {
                    this.setWaiveOfCoolingRightsErrorMessage();
                    return;
                }
                this.isExSubscriber ? this.submitExSubscriberPayment(e) : this.submitOnClicked();
            },
            updateDateForSwitchPlan(coupon) {
                const isUpgradeAnd100PercentOffPromo =
                    this.plan?.switchDirection === 'upgrade' &&
                    this.coupon.recurlyInfo?.discount?.type === 'percent' &&
                    this.coupon.recurlyInfo?.discount?.rate === 1;

                if (isUpgradeAnd100PercentOffPromo) {
                    const { temporal_unit, temporal_amount } = coupon.recurlyInfo;
                    this.calcFullCouponBillingDate(coupon, temporal_amount, temporal_unit);
                }
            },
            updateCouponBillingDate(coupon) {
                this.couponDiscountBillingDate = '';
                if (this.isSwitchPlan) {
                    this.updateDateForSwitchPlan(coupon);
                } else {
                    this.updateFirstBillingDate(coupon);
                }
            },
        },
        watch: {
            // If there is an auto-applying promo that was checked, but not valid (no recurlyInfo),
            // Attempt to handle any 100% off promos that may be on the user's current subscription
            coupon(newVal, oldVal) {
                if (newVal !== oldVal) {
                    if (newVal?.recurlyInfo) {
                        this.updateCouponBillingDate(newVal);
                    }
                }
                // The coupon has settled (stripped -> {} via resetCoupon, or preserved ->
                // recurlyInfo): re-evaluate whether to show the lose-promo ribbon.
                this.showLosePromoRibbonIfNeeded();
            },
        },
        async mounted() {
            const notificationKey = this.$route.query?.notification;
            const notificationMessage = notificationKey && this.$getLocale(notificationKey);
            if (isKnownLocaleMessage(notificationMessage, notificationKey)) {
                this.openNotification(
                    new NotificationBarModel({
                        success: true,
                        autoClose: false,
                        message: notificationMessage,
                    }),
                );
            } else if (this.currentPlan?.on_trial) {
                // Free-trial loss is known synchronously at mount. The coupon-strip case is
                // deferred to the `coupon` watch, which fires once the async coupon settles.
                this.showLosePromoRibbonIfNeeded();
            }
            if (this.coupon?.recurlyInfo) {
                this.updateCouponBillingDate(this.coupon);
            }

            if (
                this.isSwitchPlan ||
                this.$store.getters.featureIsActive(PAYMENT_EXSUB_BILLING_SUMMARY)
            ) {
                await fetchStoredPaymentIfEmpty(this.$store);

                if (this.hasValidStoredPaymentMethod) {
                    this.$store.commit(
                        'payment/setPaymentMethod',
                        this.getBillingInfo?.isTypePaypal ? PAYMENT_PAYPAL : PAYMENT_CC,
                    );
                }
            }
        },
    };
</script>
